import { useCallback, useEffect, useRef, useState } from "react";

export const MAX_SECONDS = 120;
const TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

export const useRecorder = () => {
  const [state, setState] = useState("idle"); // idle | recording | done | error
  const [seconds, setSeconds] = useState(0);
  const [clip, setClip] = useState(null);
  const recRef = useRef(null);
  const timerRef = useRef(0);
  const startRef = useRef(0);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    recRef.current?.stream?.getTracks().forEach((tr) => tr.stop());
  }, []);

  const stop = useCallback(() => {
    if (recRef.current?.state === "recording") recRef.current.stop();
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = TYPES.find((m) => window.MediaRecorder?.isTypeSupported?.(m));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = () => {
        cleanup();
        const blob = new Blob(chunks, { type: (rec.mimeType || "audio/webm").split(";")[0] });
        const duration = Math.min((Date.now() - startRef.current) / 1000, MAX_SECONDS);
        setClip({ blob, url: URL.createObjectURL(blob), duration });
        setState("done");
      };
      recRef.current = rec;
      startRef.current = Date.now();
      setSeconds(0);
      setClip(null);
      rec.start();
      setState("recording");
      timerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - startRef.current) / 1000);
        setSeconds(s);
        if (s >= MAX_SECONDS) rec.stop();
      }, 250);
    } catch {
      setState("error");
    }
  }, [cleanup]);

  const reset = useCallback(() => {
    setClip((c) => { if (c) URL.revokeObjectURL(c.url); return null; });
    setSeconds(0);
    setState("idle");
  }, []);

  useEffect(() => () => {
    if (recRef.current?.state === "recording") { recRef.current.onstop = null; recRef.current.stop(); }
    cleanup();
  }, [cleanup]);

  return { state, seconds, clip, start, stop, reset };
};

export const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
